import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Camera, CheckCircle, Circle, Clock, Copy, Download, History, PlugZap, RefreshCw, Trash2, Upload } from 'lucide-react';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { Badge } from 'components/common/Badge';
import { Button } from 'components/common/Button';
import { Card } from 'components/common/Card';
import { LoadingScreen } from 'components/common/Spinner';
import { StoreDataService } from 'services/data/store-data.service';
import { connectionService } from 'services/integrations/connection.service';
import { DataHealth, FeedStatus } from 'dtos/data/data-health.dto';
import { POSConnection } from 'dtos/integrations/connection.dto';
import { POS_OPTIONS, PosOption, posOptionFor } from 'constants/posCatalog';
import { formatDateTime } from 'utils/formatters';

const INSTALLER_URL = 'https://fqygopfflgckjkilqsqp.supabase.co/storage/v1/object/public/pellvr-agent/Pellvr-Agent-Setup-0.1.2.exe';

const STATUS_STYLE: Record<FeedStatus, { label: string; variant: 'success' | 'warning' | 'danger' | 'default'; icon: React.ElementType; color: string }> = {
  ok: { label: 'Good', variant: 'success', icon: CheckCircle, color: 'text-green-600' },
  partial: { label: 'Incomplete', variant: 'warning', icon: AlertTriangle, color: 'text-amber-500' },
  stale: { label: 'Out of date', variant: 'warning', icon: Clock, color: 'text-amber-500' },
  missing: { label: 'Not added yet', variant: 'default', icon: Circle, color: 'text-gray-300' },
};

const posKey = (storeId: string) => `pellvr.pos.${storeId}`;
function readSavedPos(storeId: string): string | null {
  try { return localStorage.getItem(posKey(storeId)); } catch { return null; }
}
function savePos(storeId: string, id: string) {
  try { localStorage.setItem(posKey(storeId), id); } catch { /* per-browser convenience only */ }
}

function copy(text: string) { void navigator.clipboard?.writeText(text); }

function FeedRow({ title, status, message, hint, action }: { title: string; status: FeedStatus; message: string; hint?: string; action: React.ReactNode }) {
  const style = STATUS_STYLE[status];
  const Icon = style.icon;
  return (
    <li className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <Icon size={20} className={`mt-0.5 flex-shrink-0 ${style.color}`} />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2"><h2 className="text-sm font-semibold text-gray-900">{title}</h2><Badge variant={style.variant}>{style.label}</Badge></div>
          <p className="mt-0.5 text-sm text-gray-600">{message}</p>
          {status !== 'ok' && hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
        </div>
      </div>
      {status !== 'ok' && <div className="flex flex-shrink-0 flex-wrap gap-2 pl-8 sm:pl-0">{action}</div>}
    </li>
  );
}

function SetupCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
      <p className="text-xs font-medium text-blue-900">Setup code: paste this into the Pellvr Agent. It is shown only once.</p>
      <div className="mt-2 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1.5 text-xs text-gray-800" title={code}>{code}</code>
        <Button size="sm" variant="secondary" onClick={() => { copy(code); setCopied(true); }}><Copy size={14} /> {copied ? 'Copied' : 'Copy'}</Button>
      </div>
    </div>
  );
}

function connectionState(connection: POSConnection): { label: string; variant: 'success' | 'warning' | 'danger'; help: string } {
  if (connection.status === 'healthy') return { label: 'Connected', variant: 'success', help: 'The agent is checking in. New export files upload automatically.' };
  if (connection.status === 'degraded' || connection.status === 'failed') return { label: 'Needs attention', variant: 'danger', help: 'The agent is connected but the last file could not be read. Check the upload history for details.' };
  return { label: 'Waiting for the agent', variant: 'warning', help: 'Install the agent on the back-office PC and paste the setup code.' };
}

function AgentSetup({ option, connection, canManage, isOwner, onChanged }: { option: PosOption; connection: POSConnection | null; canManage: boolean; isOwner: boolean; onChanged: () => void }) {
  const { org, currentStore } = useStore();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const create = async () => {
    if (!org || !currentStore || !option.provider) return;
    setBusy(true); setError('');
    try {
      const result = await connectionService.create(org.id, { store_id: currentStore.id, provider: option.provider, transport: 'local_agent' });
      setCode(result.setup_code);
      onChanged();
    } catch (err: any) { setError(err?.message || 'Could not create a setup code. Please try again.'); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    if (!org || !connection) return;
    if (!window.confirm('Create a new setup code? The agent currently paired with this store will stop uploading until you paste the new code into it.')) return;
    setBusy(true); setError('');
    try { setCode((await connectionService.provisionToken(org.id, connection.id)).setup_code); }
    catch (err: any) { setError(err?.message || 'Could not create a new setup code.'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!org || !connection || !window.confirm(`Disconnect the Pellvr Agent from ${currentStore?.name}? Files already uploaded are kept.`)) return;
    setBusy(true); setError('');
    try { await connectionService.delete(org.id, connection.id); setCode(''); onChanged(); }
    catch (err: any) { setError(err?.message || 'Could not disconnect the agent.'); }
    finally { setBusy(false); }
  };

  const state = connection ? connectionState(connection) : null;
  return (
    <div className="space-y-4">
      {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {connection && state && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 p-3">
          <div>
            <div className="flex items-center gap-2"><PlugZap size={16} className="text-gray-500" /><span className="text-sm font-medium text-gray-900">Pellvr Agent</span><Badge variant={state.variant}>{state.label}</Badge></div>
            <p className="mt-1 text-xs text-gray-500">{state.help}{connection.updated_at ? ` Last update ${formatDateTime(connection.updated_at)}.` : ''}</p>
          </div>
          <div className="flex gap-2">
            {isOwner && <Button size="sm" variant="secondary" onClick={() => void regenerate()} disabled={busy}><RefreshCw size={14} /> New setup code</Button>}
            {canManage && <Button size="sm" variant="ghost" onClick={() => void remove()} disabled={busy}><Trash2 size={14} /> Disconnect</Button>}
          </div>
        </div>
      )}
      <ol className="space-y-3 text-sm text-gray-700">
        <li className="flex gap-3"><span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">1</span>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900">Get a setup code</p>
            {code ? <div className="mt-2"><SetupCode code={code} /></div>
              : connection ? <p className="text-gray-500">This store already has a setup code. {isOwner ? 'Create a new one if you lost it.' : 'Ask the account owner if you need a new one.'}</p>
              : canManage ? <div className="mt-2"><Button size="sm" onClick={() => void create()} loading={busy}>Create setup code</Button></div>
              : <p className="text-gray-500">Ask the account owner or a manager to create one.</p>}
          </div>
        </li>
        <li className="flex gap-3"><span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">2</span>
          <div><p className="font-medium text-gray-900">Install the Pellvr Agent on the back-office PC</p><p className="text-gray-500">The Windows PC in the office that your POS sends reports to. It needs internet access.</p>
            <a className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline" href={INSTALLER_URL} target="_blank" rel="noreferrer"><Download size={14} /> Download the agent (Windows)</a></div>
        </li>
        <li className="flex gap-3"><span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">3</span>
          <div><p className="font-medium text-gray-900">Paste the code, pick "{option.name}" and choose the export folder</p><p className="text-gray-500">{option.priceBook[0]}</p></div>
        </li>
        <li className="flex gap-3"><span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">4</span>
          <div><p className="font-medium text-gray-900">That's it</p><p className="text-gray-500">{option.sales[0]} The status above turns green when the agent checks in.</p></div>
        </li>
      </ol>
      <p className="text-xs text-gray-500">Stuck, or the folder is on another computer? <Link className="font-medium text-blue-600 hover:underline" to="/imports">Upload an export file</Link> in the meantime (CSV, Excel or POS XML). You can switch to automatic later.</p>
    </div>
  );
}

function UploadGuide({ option }: { option: PosOption }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      <ol className="space-y-2 text-sm text-gray-700">
        <li><span className="font-medium text-gray-900">Item list and prices:</span> {option.priceBook.join(' ')}</li>
        <li><span className="font-medium text-gray-900">Item sales:</span> {option.sales.join(' ')}</li>
      </ol>
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" onClick={() => navigate('/imports')}><Upload size={14} /> Upload files</Button>
        <p className="text-xs text-gray-500">Drop them as they come: CSV, Excel or POS XML. Pellvr recognizes which is which.</p>
      </div>
    </div>
  );
}

export default function StoreDataPage() {
  const { org, currentStore } = useStore();
  const navigate = useNavigate();
  const canManage = org?.role === 'owner' || org?.role === 'manager';
  const isOwner = org?.role === 'owner';

  const { data: health, loading, error, refetch } = useApi<DataHealth>(
    () => StoreDataService.health(org?.id ?? '', currentStore?.id ?? ''),
    [org?.id, currentStore?.id]
  );
  const [connections, setConnections] = useState<POSConnection[]>([]);
  const loadConnections = useCallback(async () => {
    if (!org) return;
    try { setConnections(await connectionService.list(org.id)); } catch { setConnections([]); }
  }, [org]);
  useEffect(() => { void loadConnections(); }, [loadConnections]);

  const agentConnection = useMemo(
    () => connections.find(connection => connection.store_id === currentStore?.id && connection.transport === 'local_agent') ?? null,
    [connections, currentStore?.id]
  );
  const [posId, setPosId] = useState<string | null>(null);
  useEffect(() => {
    if (!currentStore) return;
    const fromConnection = agentConnection ? POS_OPTIONS.find(option => option.provider === agentConnection.provider)?.id : null;
    setPosId(fromConnection ?? readSavedPos(currentStore.id));
  }, [currentStore, agentConnection]);
  const option = posOptionFor(posId);
  const choose = (id: string) => { if (currentStore) savePos(currentStore.id, id); setPosId(id); };

  if (loading && !health) return <LoadingScreen />;

  const missingCount = health ? [health.price_book, health.sales, health.costs].filter(feed => feed.status !== 'ok').length : 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Store data</h1>
          <p className="text-sm text-gray-500">
            {!health ? `What Pellvr knows about ${currentStore?.name ?? 'your store'}.`
              : health.ready_for_alerts ? 'Everything is in. Pellvr checks your store every night.'
              : `${missingCount} of 3 still needed. Pellvr runs its first profit check a few minutes after all three are in.`}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/imports')}><History size={15} /> Upload history</Button>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">We couldn't check your data right now. {error} <button className="font-semibold underline" onClick={() => { refetch(); void loadConnections(); }}>Try again</button></div>}

      {health && (
        <Card>
          <ul className="divide-y divide-gray-100">
            <FeedRow title="Item list and prices" status={health.price_book.status} message={health.price_book.message}
              action={<Button size="sm" onClick={() => navigate('/imports?type=CATALOG')}><Upload size={14} /> Upload</Button>} />
            <FeedRow title="Item sales" status={health.sales.status} message={health.sales.message}
              action={<Button size="sm" onClick={() => navigate('/imports?type=SALES')}><Upload size={14} /> Upload</Button>} />
            <FeedRow title="Supplier costs" status={health.costs.status} message={health.costs.message}
              hint="Photograph invoices from direct-delivery vendors. Download wholesaler (Core-Mark, McLane) invoice files and upload them as they are."
              action={health.costs.documents_needing_review > 0
                ? <Button size="sm" onClick={() => navigate('/costs/pending')}>Review invoices</Button>
                : <>
                    <Button size="sm" onClick={() => navigate('/costs/capture')}><Camera size={14} /> Photo</Button>
                    <Button size="sm" variant="secondary" onClick={() => navigate('/imports')}><Upload size={14} /> File</Button>
                  </>} />
          </ul>
        </Card>
      )}

      <Card>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Send prices and sales from your POS</h2>
            <p className="text-xs text-gray-500">Verifone and Gilbarco Passport can send them automatically (beta). Every other POS uploads its export files.</p>
          </div>
          <select aria-label="Your POS" value={posId ?? ''} onChange={event => choose(event.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
            <option value="" disabled>Choose your POS…</option>
            {POS_OPTIONS.map(choice => <option key={choice.id} value={choice.id}>{choice.name}{choice.method === 'agent' ? ' (automatic)' : ''}</option>)}
          </select>
        </div>
        {option && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            {option.method === 'agent'
              ? <AgentSetup option={option} connection={agentConnection} canManage={canManage} isOwner={isOwner} onChanged={() => void loadConnections()} />
              : <UploadGuide option={option} />}
            {option.note && <p className="mt-4 text-xs text-gray-500">{option.note}</p>}
          </div>
        )}
      </Card>
    </div>
  );
}
